"use client";
import { type ReactNode } from "react";
import ErrorMessage from "@/components/common/formElements/controlled/ErrorMessage";
import {
  useCountryCodeFieldController,
  type UseCountryCodeFieldControllerProps,
} from "@/components/common/formElements/hooks/useCountryCodeFieldController";
import CountryCodeSelector, {
  type CountryCodeSelectorProps,
} from "@/components/common/formElements/library/CountryCodeSelector";
import type { FieldValues } from "react-hook-form";
import type { CountryIso2 } from "react-international-phone";

type CountryCodeSelectorPropsWithControllerProps<
  TFieldValues extends FieldValues,
> = UseCountryCodeFieldControllerProps<TFieldValues> &
  Omit<
    CountryCodeSelectorProps,
    | "name"
    | "value"
    | "defaultValue"
    | "disabled"
    | "onChange"
    | "onBlur"
    | "error"
  >;

export interface CountryCodeFieldProps<TFieldValues extends FieldValues>
  extends CountryCodeSelectorPropsWithControllerProps<TFieldValues> {
  label?: ReactNode;
  helperText?: ReactNode;
  placeholder?: string;
}

export default function CountryCodeField<TFieldValues extends FieldValues>(
  props: CountryCodeFieldProps<TFieldValues>,
) {
  const {
    name,
    onChange,
    onBlur,
    label,
    placeholder,
    helperText,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    tooltip,
    ...restCountryCodeFieldProps
  } = props;

  const {
    field,
    required,
    onBlurField,
    onChangeField,
    formState: { errors },
    fieldState: { invalid: error },
  } = useCountryCodeFieldController<TFieldValues>({
    name,
    onChange,
    onBlur,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
  });

  return (
    <CountryCodeSelector
      name={field.name}
      value={field.value as CountryIso2}
      disabled={field.disabled}
      inputRef={field.ref}
      onChange={onChangeField}
      onBlur={onBlurField}
      error={error}
      helperText={
        error ? <ErrorMessage name={field.name} errors={errors} /> : helperText
      }
      required={required}
      label={label}
      placeholder={placeholder}
      tooltip={tooltip}
      {...restCountryCodeFieldProps}
    />
  );
}
